/**
 * Custom Measurement Popup - Clean Architecture
 * Handles popup functionality for collecting custom size measurements
 */

console.log('[UNIQUE-LOG-V14] custom-measurement-popup.js loaded with smart size matching - January 2025');

// Add error handler for debugging
window.addEventListener('error', (event) => {
  if (event.filename && event.filename.includes('custom-measurement-popup')) {
    console.error('[CustomMeasurementPopup] Script error:', {
      message: event.message,
      line: event.lineno,
      column: event.colno,
      file: event.filename
    });
  }
});

/**
 * Manages persistent storage of custom sizes
 */
class CustomSizeStorage {
  constructor() {
    this.storageKey = 'customSizes';
  }
  
  getAllSizes() {
    try {
      const sizes = localStorage.getItem(this.storageKey);
      return sizes ? JSON.parse(sizes) : [];
    } catch (e) {
      console.error('[CustomSizeStorage] Error reading sizes:', e);
      return [];
    }
  }
  
  addSize(sizeData) {
    try {
      const sizes = this.getAllSizes();
      sizes.push(sizeData);
      localStorage.setItem(this.storageKey, JSON.stringify(sizes));
    } catch (e) {
      console.error('[CustomSizeStorage] Error saving size:', e);
    }
  }
  
  removeSize(sizeName) {
    try {
      const sizes = this.getAllSizes();
      const filteredSizes = sizes.filter(size => size.name !== sizeName);
      localStorage.setItem(this.storageKey, JSON.stringify(filteredSizes));
    } catch (e) {
      console.error('[CustomSizeStorage] Error removing size:', e);
    }
  }
  
  updateSize(oldName, newSizeData) {
    try {
      const sizes = this.getAllSizes();
      const index = sizes.findIndex(size => size.name === oldName);
      if (index !== -1) {
        sizes[index] = newSizeData;
        localStorage.setItem(this.storageKey, JSON.stringify(sizes));
      }
    } catch (e) {
      console.error('[CustomSizeStorage] Error updating size:', e);
    }
  }
  
  sizeExists(sizeName) {
    const sizes = this.getAllSizes();
    return sizes.some(size => size.name === sizeName);
  }
  
  getSize(sizeName) {
    const sizes = this.getAllSizes();
    return sizes.find(size => size.name === sizeName);
  }
}

/**
 * Smart size matching for lead time calculation
 */
class SizeMatcher {
  constructor() {
    this.standardSizes = null;
    this.inventorySettings = null;
    this.tolerance = 4; // 4 inches tolerance for alterations
    this.loadData();
  }
  
  loadData() {
    // Load standard sizes from metafields
    const sizesData = document.getElementById('standard-sizes-data');
    if (sizesData) {
      try {
        this.standardSizes = JSON.parse(sizesData.textContent);
        console.log('[SizeMatcher] Loaded standard sizes:', this.standardSizes);
      } catch (e) {
        console.error('[SizeMatcher] Error parsing standard sizes:', e);
      }
    }
    
    // Load inventory settings
    const inventoryData = document.getElementById('inventory-settings-data');
    if (inventoryData) {
      try {
        this.inventorySettings = JSON.parse(inventoryData.textContent);
        console.log('[SizeMatcher] Loaded inventory settings:', this.inventorySettings);
      } catch (e) {
        console.error('[SizeMatcher] Error parsing inventory settings:', e);
      }
    }
  }
  
  parseMetafieldValue(value) {
    // Parse format: "Label;small;medium;large"
    if (!value) return null;
    const parts = value.split(';');
    if (parts.length !== 4) return null;
    
    return {
      label: parts[0],
      small: parseFloat(parts[1]),
      medium: parseFloat(parts[2]),
      large: parseFloat(parts[3])
    };
  }
  
  convertToInches(value, unit) {
    // Convert cm to inches if needed
    if (unit === 'cm') {
      return value / 2.54;
    }
    return value;
  }
  
  convertCmToInches(cmValue) {
    // Convert CM value to inches
    return cmValue / 2.54;
  }
  
  checkSizeMatch(measurements, unit) {
    console.log('[SIZE-MATCH-DEBUG] Starting size matching process');
    console.log('[SIZE-MATCH-DEBUG] Input measurements:', measurements);
    console.log('[SIZE-MATCH-DEBUG] Input unit:', unit);
    console.log('[SIZE-MATCH-DEBUG] Available standard sizes:', this.standardSizes);
    
    if (!this.standardSizes || !measurements) {
      console.log('[SIZE-MATCH-DEBUG] No standard sizes or measurements available');
      return null;
    }
    
    // Field mapping from form field names to metafield names
    const fieldMapping = {
      'chest': 'chest',       // Form field "chest" maps to metafield "chest"
      'shoulders': 'shoulders',
      'sleeves': 'sleeves',   // Form field "sleeves" maps to metafield "sleeves"
      'hips': 'hips',         // Form field "hips" maps to metafield "hips"
      'waist': 'waist',       // Form field "waist" maps to metafield "waist"
      'trouser_length': 'trouserlength',
      'shirt_length': null,   // No corresponding metafield for shirt length
      'kameez_length': null,  // No metafield yet — once products have kameezlength_size, change to 'kameezlength'
      'lehenga_length': null  // No metafield yet — once products have lehengalength_size, change to 'lehengalength'
    };
    
    console.log('[SIZE-MATCH-DEBUG] Field mapping:', fieldMapping);
    
    // Check each standard size (small, medium, large)
    const sizes = ['small', 'medium', 'large'];
    
    for (const size of sizes) {
      console.log(`[SIZE-MATCH-DEBUG] Checking ${size} size...`);
      let allWithinTolerance = true;
      const matchDetails = {};
      
      // Check each measurement field
      for (const [formField, metafieldName] of Object.entries(fieldMapping)) {
        if (!metafieldName || !measurements[formField]) {
          console.log(`[SIZE-MATCH-DEBUG]   Skipping ${formField}: ${!metafieldName ? 'no metafield mapping' : 'no measurement value'}`);
          continue;
        }
        
        const metafieldData = this.parseMetafieldValue(this.standardSizes[metafieldName]);
        console.log(`[SIZE-MATCH-DEBUG]   ${formField} -> ${metafieldName}: parsed data =`, metafieldData);
        
        if (!metafieldData) {
          console.log(`[SIZE-MATCH-DEBUG]   Skipping ${formField}: could not parse metafield data`);
          continue;
        }
        
        const standardValue = metafieldData[size];
        if (isNaN(standardValue)) {
          console.log(`[SIZE-MATCH-DEBUG]   Skipping ${formField}: invalid standard value (${standardValue})`);
          continue;
        }
        
        // Convert customer measurement to inches for comparison
        const customerValue = this.convertToInches(parseFloat(measurements[formField]), unit);
        
        // Convert metafield value from CM to inches (metafields store values in CM)
        const standardValueInches = this.convertCmToInches(standardValue);
        
        // Check if within tolerance (±4 inches)
        const difference = Math.abs(customerValue - standardValueInches);
        
        console.log(`[SIZE-MATCH-DEBUG]   ${formField}: customer=${measurements[formField]}${unit} (${customerValue.toFixed(2)}") vs standard=${standardValue}cm (${standardValueInches.toFixed(2)}") diff=${difference.toFixed(2)}" tolerance=${this.tolerance}"`);
        
        matchDetails[formField] = {
          customerInput: measurements[formField],
          customerInches: customerValue,
          standardCm: standardValue,
          standardInches: standardValueInches,
          difference: difference,
          withinTolerance: difference <= this.tolerance
        };
        
        if (difference > this.tolerance) {
          console.log(`[SIZE-MATCH-DEBUG]   ${formField} FAILS tolerance check`);
          allWithinTolerance = false;
          break;
        } else {
          console.log(`[SIZE-MATCH-DEBUG]   ${formField} PASSES tolerance check`);
        }
      }
      
      console.log(`[SIZE-MATCH-DEBUG] ${size} size match details:`, matchDetails);
      
      if (allWithinTolerance) {
        console.log(`[SIZE-MATCH-DEBUG] ✅ MATCH FOUND with ${size} size!`);
        console.log(`[SizeMatcher] Match found with ${size} size`);
        return 'in_stock';
      } else {
        console.log(`[SIZE-MATCH-DEBUG] ❌ ${size} size does not match`);
      }
    }
    
    console.log('[SIZE-MATCH-DEBUG] ❌ NO MATCH FOUND - custom size needed');
    console.log('[SizeMatcher] No match found, custom size needed');
    return 'backorder';
  }
  
  calculateLeadTime(status) {
    if (!this.inventorySettings) return null;
    
    const leadTime = this.inventorySettings.leadTime || 10;
    const backorderDays = this.inventorySettings.backorderDays || 30;
    
    const totalDays = status === 'in_stock' ? leadTime : leadTime + backorderDays;
    
    // Calculate delivery date
    const today = new Date();
    const deliveryDate = new Date(today.getTime() + (totalDays * 24 * 60 * 60 * 1000));
    
    // Format date like "29th January"
    const day = deliveryDate.getDate();
    const month = deliveryDate.toLocaleDateString('en-US', { month: 'long' });
    
    // Add ordinal suffix
    const ordinal = this.getOrdinalSuffix(day);
    const formattedDate = `${day}${ordinal} ${month}`;
    
    return {
      status: status,
      deliveryDate: formattedDate,
      message: status === 'in_stock' ? 
        this.inventorySettings.inStockMessage : 
        this.inventorySettings.backorderMessage
    };
  }
  
  getOrdinalSuffix(day) {
    const lastDigit = day % 10;
    const lastTwoDigits = day % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }
    
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }
}

/**
 * Manages the display and selection of custom size pills
 */
class CustomSizeSelector {
  constructor(storage) {
    this.storage = storage;
    this.selectedCustomSize = null;
    this.container = null;
    
    // Store custom sizes globally so they survive DOM morphing
    window.__customSizes = this.storage.getAllSizes();
    
    this.init();
  }
  
  init() {
    console.log('[CustomSizeSelector] Initializing...');
    this.findContainer();
    this.renderCustomSizes();
    
    // Always setup event listeners so we can handle DOM morphing
    this.setupEventListeners();
    this.setupContainerPersistence();
    
    const customSizes = this.storage.getAllSizes();
    if (customSizes.length === 0) {
      console.log('[CustomSizeSelector] No custom sizes found yet');
    }
  }
  
  findContainer() {
    // Try multiple possible container locations
    const selectors = [
      'fieldset.variant-option--buttons legend:contains("Size")',
      'fieldset.variant-option--buttons:has(legend:contains("Size"))',
      'fieldset:has(legend:contains("Size"))',
      '.variant-option:has(legend:contains("Size"))',
      'fieldset.variant-option',
      '.size-selector',
      '.variant-picker fieldset'
    ];
    
    for (const selector of selectors) {
      let elements;
      if (selector.includes(':contains("Size")')) {
        // Manual search for Size legend
        const fieldsets = document.querySelectorAll('fieldset');
        elements = Array.from(fieldsets).filter(fieldset => {
          const legend = fieldset.querySelector('legend');
          return legend && legend.textContent.includes('Size');
        });
      } else {
        elements = document.querySelectorAll(selector);
      }
      
      if (elements.length > 0) {
        this.container = elements[0];
        console.log('[CustomSizeSelector] Found container with selector:', selector);
        break;
      }
    }
    
    if (!this.container) {
      console.warn('[CustomSizeSelector] Could not find size container');
    }
  }
  
  renderCustomSizes() {
    // Always try to find container again in case DOM changed
    if (!this.container || !document.body.contains(this.container)) {
      this.findContainer();
    }
    
    if (!this.container) {
      console.warn('[CustomSizeSelector] No container found');
      return;
    }
    
    // Use global store which survives DOM morphing
    const customSizes = window.__customSizes || [];
    if (customSizes.length === 0) {
      console.log('[CustomSizeSelector] No custom sizes to render');
      return;
    }
    
    console.log('[CustomSizeSelector] Rendering custom sizes:', customSizes.length);
    
    // Remove all existing custom size pills
    const existingPills = document.querySelectorAll('.custom-size-pill');
    existingPills.forEach(pill => pill.remove());
    
    // Also remove any orphaned wrappers
    const oldWrappers = document.querySelectorAll('.custom-sizes-wrapper');
    oldWrappers.forEach(wrapper => wrapper.remove());
    
    // Create a fresh wrapper to hold all custom sizes
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-sizes-wrapper';
    wrapper.style.cssText = 'display: contents;'; // Invisible wrapper
    wrapper.setAttribute('data-custom-sizes', 'true'); // Mark for identification
    
    // Insert wrapper before the Add Custom Size button
    const addCustomButton = this.container.querySelector('input[value="Custom"], input[value="Add Custom Size"]');
    if (addCustomButton) {
      const buttonContainer = addCustomButton.closest('label') || addCustomButton.closest('div') || addCustomButton.parentElement;
      if (buttonContainer && buttonContainer.parentElement) {
        buttonContainer.parentElement.insertBefore(wrapper, buttonContainer);
      } else {
        this.container.appendChild(wrapper);
      }
    } else {
      this.container.appendChild(wrapper);
    }
    
    // Add each custom size as a pill to the wrapper
    customSizes.forEach(size => {
      this.addCustomSizePill(size, wrapper);
    });
    
    // Dispatch custom event to notify other components
    document.dispatchEvent(new CustomEvent('customsize:rendered', {
      detail: { count: customSizes.length }
    }));
  }
  
  addCustomSizePill(sizeData, targetContainer) {
    const container = targetContainer || this.container;
    if (!container) return;
    
    const pillId = `custom-size-${sizeData.name.replace(/\s+/g, '-').toLowerCase()}`;
    
    // Create a label element that exactly matches Shopify's structure
    const label = document.createElement('label');
    label.className = 'variant-option__button-label custom-size-pill';
    label.dataset.customSize = sizeData.name;
    
    // Add visual selection if this is the currently selected custom size
    if (this.selectedCustomSize === sizeData.name) {
      label.classList.add('visually-selected');
    }
    
    // Find the correct radio name from existing size inputs
    const existingSizeRadio = document.querySelector('input[type="radio"][value="Small"], input[type="radio"][value="Medium"], input[type="radio"][value="Large"]');
    const radioName = existingSizeRadio ? existingSizeRadio.name : 'Size';
    
    // Create the structure - use a different name to avoid conflicts
    label.innerHTML = `
      <input type="radio" 
             name="custom-sizes-only" 
             value="${sizeData.name}" 
             aria-label="${sizeData.name}"
             data-input-id="custom-${sizeData.name}"
             data-option-value-id="custom-${sizeData.name.replace(/\s+/g, '-').toLowerCase()}"
             data-option-available="true"
             class="custom-size-radio"
             disabled>
      <span class="variant-option__button-label__text custom-size-text">
        ${sizeData.name}
        <button type="button" class="custom-size-delete" data-size="${sizeData.name}">×</button>
      </span>
    `;
    
    // Add to the target container
    container.appendChild(label);
    
    console.log('[CustomSizeSelector] Added custom size pill:', sizeData.name);
  }
  
  setupEventListeners() {
    // Handle clicks on custom size pills (use event delegation)
    document.addEventListener('click', (e) => {
      // Handle label clicks for custom sizes
      const customLabel = e.target.closest('label.custom-size-pill');
      if (customLabel && !e.target.classList.contains('custom-size-delete')) {
        e.preventDefault(); // Prevent default label behavior
        const sizeName = customLabel.dataset.customSize;
        this.selectCustomSize(sizeName);
      }
      
      
      // Handle delete buttons
      if (e.target.classList.contains('custom-size-delete')) {
        e.preventDefault();
        e.stopPropagation();
        const sizeName = e.target.dataset.size;
        this.deleteCustomSize(sizeName);
      }
    });
    
    // Monitor for radio changes to update selected state
    document.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && (e.target.name.includes('Size') || e.target.name === 'option1')) {
        const isCustomRadio = e.target.classList.contains('custom-size-radio');
        const isAddCustomButton = e.target.value === 'Custom' || e.target.value === 'Add Custom Size';
        const isCustomValue = e.target.value.startsWith('custom-size-');
        
        if (!isCustomRadio) {
          if (isCustomValue) {
            // This is our custom size value being set by variant picker - ignore
            return;
          } else if (isAddCustomButton) {
            // Add Custom Size clicked directly - ensure it's not invisible
            const label = e.target.closest('label');
            if (label && label.classList.contains('invisible-selected')) {
              // This was triggered by custom size selection, ignore
              return;
            }
            // User clicked Add Custom Size directly - clear custom selections
            this.deselectAllCustomSizes();
          } else {
            // Standard size selected, clear all custom selections
            this.deselectAllCustomSizes();
            // Restore Add to Cart button when switching away from Add Custom Size
            if (window.customMeasurementPopup) {
              window.customMeasurementPopup.restoreAddToCartButtons();
            }
          }
        }
      }
    });
  }
  
  selectCustomSize(sizeName) {
    console.log('[CustomSizeSelector] Selecting custom size:', sizeName);
    this.selectedCustomSize = sizeName;
    
    // Update the hidden input for the custom size name
    this.updateCustomSizeInput(sizeName);
    
    // Add measurement properties from the selected custom size
    this.addMeasurementProperties(sizeName);
    
    // First, invisibly select the "Add Custom Size" button for Shopify
    const addCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]');
    if (addCustomSizeRadio) {
      // Check it but mark as invisible
      addCustomSizeRadio.checked = true;
      const addCustomLabel = addCustomSizeRadio.closest('label');
      if (addCustomLabel) {
        addCustomLabel.classList.add('invisible-selected');
      }
      
      // Trigger change event so Shopify knows a valid variant is selected
      const event = new Event('change', { bubbles: true });
      addCustomSizeRadio.dispatchEvent(event);
    }
    
    // Now handle visual selection of custom size
    // Remove visual selection from all pills first
    document.querySelectorAll('.custom-size-pill').forEach(pill => {
      pill.classList.remove('visually-selected');
    });
    
    // Add visual selection to the selected custom size
    const selectedPill = document.querySelector(`[data-custom-size="${sizeName}"]`);
    if (selectedPill) {
      selectedPill.classList.add('visually-selected');
    }
    
    // Ensure custom radio buttons are NOT actually checked (to avoid conflicts)
    document.querySelectorAll('.custom-size-radio').forEach(radio => {
      radio.checked = false;
    });
    
    // Restore Add to Cart button since we have a valid custom size selected
    if (window.customMeasurementPopup) {
      window.customMeasurementPopup.restoreAddToCartButtons();
    }
  }
  
  deselectAllCustomSizes() {
    console.log('[CustomSizeSelector] Deselecting all custom sizes');
    this.selectedCustomSize = null;
    
    // Clear the hidden input for the custom size name
    this.updateCustomSizeInput('');
    
    // Remove measurement properties
    this.removeMeasurementProperties();
    
    // Remove visual selection from all custom pills
    document.querySelectorAll('.custom-size-pill').forEach(pill => {
      pill.classList.remove('visually-selected');
    });
    
    // Remove invisible selection from Add Custom Size
    const addCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]');
    if (addCustomSizeRadio) {
      const addCustomLabel = addCustomSizeRadio.closest('label');
      if (addCustomLabel) {
        addCustomLabel.classList.remove('invisible-selected');
      }
    }
    
    // Ensure custom radio buttons are unchecked
    const customSizes = document.querySelectorAll('.custom-size-radio');
    customSizes.forEach(radio => {
      radio.checked = false;
    });
  }
  
  updateCustomSizeInput(sizeName) {
    console.log('[CustomSizeSelector] Updating custom size input with:', sizeName);
    
    // Find the product form
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]') || 
                       document.querySelector('form[action="/cart/add"]') ||
                       document.querySelector('product-form form');
    
    if (!productForm) {
      console.error('[CustomSizeSelector] Could not find product form');
      return;
    }
    
    // Find or create the hidden input for custom size
    let customSizeInput = productForm.querySelector('input[name="properties[Custom Size]"]');
    
    if (!customSizeInput && sizeName) {
      // Create the input if it doesn't exist and we have a size name
      customSizeInput = document.createElement('input');
      customSizeInput.type = 'hidden';
      customSizeInput.name = 'properties[Custom Size]';
      productForm.appendChild(customSizeInput);
      console.log('[CustomSizeSelector] Created hidden input for custom size');
    }
    
    if (customSizeInput) {
      if (sizeName) {
        // Set the value to the custom size name
        customSizeInput.value = sizeName;
        console.log('[CustomSizeSelector] Set custom size input value to:', sizeName);
      } else {
        // Remove the input if no size is selected
        customSizeInput.remove();
        console.log('[CustomSizeSelector] Removed custom size input');
      }
    }
  }
  
  addMeasurementProperties(sizeName) {
    console.log('[CustomSizeSelector] Adding measurement properties for:', sizeName);
    
    // Get the size data from storage
    const sizeData = this.storage.getSize(sizeName);
    if (!sizeData || !sizeData.measurements) {
      console.error('[CustomSizeSelector] No size data found for:', sizeName);
      return;
    }
    
    // Find the product form
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]') || 
                       document.querySelector('form[action="/cart/add"]') ||
                       document.querySelector('product-form form');
    
    if (!productForm) {
      console.error('[CustomSizeSelector] Could not find product form');
      return;
    }
    
    // Remove any existing measurement inputs first
    this.removeMeasurementProperties();
    
    // Add measurements as hidden inputs with underscore prefix
    const measurementFields = [
      { key: 'chest', name: '_Chest' },
      { key: 'shoulders', name: '_Shoulders' },
      { key: 'hips', name: '_Hips' },
      { key: 'waist', name: '_Waist' },
      { key: 'sleeves', name: '_Sleeves' },
      { key: 'trouser_length', name: '_Trouser Length' },
      { key: 'shirt_length', name: '_Shirt Length' },
          { key: 'kameez_length', name: '_Kameez Length' },
      { key: 'lehenga_length', name: '_Lehenga Length' }
    ];
    
    measurementFields.forEach(field => {
      const value = sizeData.measurements[field.key];
      if (value !== null && value !== undefined) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${field.name}]`;
        input.value = `${value} ${sizeData.unit || 'cm'}`;
        input.className = 'custom-measurement-property';
        productForm.appendChild(input);
        console.log(`[CustomSizeSelector] Added measurement: ${field.name} = ${value} ${sizeData.unit || 'cm'}`);
      }
    });
  }
  
  removeMeasurementProperties() {
    console.log('[CustomSizeSelector] Removing measurement properties');
    
    // Find the product form
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]') || 
                       document.querySelector('form[action="/cart/add"]') ||
                       document.querySelector('product-form form');
    
    if (!productForm) {
      console.error('[CustomSizeSelector] Could not find product form');
      return;
    }
    
    // Remove all measurement property inputs
    const measurementInputs = productForm.querySelectorAll('.custom-measurement-property');
    measurementInputs.forEach(input => {
      input.remove();
      console.log('[CustomSizeSelector] Removed measurement input:', input.name);
    });
    
    // Also remove any inputs with underscore prefix in the name
    const underscoreInputs = productForm.querySelectorAll('input[name^="properties[_"]');
    underscoreInputs.forEach(input => {
      if (input.name.includes('_Chest') || input.name.includes('_Shoulders') || 
          input.name.includes('_Hips') || input.name.includes('_Waist') || 
          input.name.includes('_Sleeves') || input.name.includes('_Trouser') || 
          input.name.includes('_Shirt') || input.name.includes('_Kameez') || 
          input.name.includes('_Lehenga')) {
        input.remove();
        console.log('[CustomSizeSelector] Removed underscore input:', input.name);
      }
    });
  }
  
  deleteCustomSize(sizeName) {
    // Create custom confirmation dialog
    const confirmDialog = document.createElement('div');
    confirmDialog.className = 'custom-size-confirm-popup';
    confirmDialog.innerHTML = `
      <div class="confirm-overlay"></div>
      <div class="confirm-content">
        <h3>Delete Custom Size</h3>
        <p>Are you sure you want to delete the custom size "${sizeName}"?</p>
        <div class="confirm-buttons">
          <button class="confirm-yes">Delete</button>
          <button class="confirm-no">Cancel</button>
        </div>
        <div class="edit-option">
          <p>Want to change the measurements instead?</p>
          <button class="confirm-edit">Edit Measurements</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(confirmDialog);
    
    // Handle overlay click
    confirmDialog.querySelector('.confirm-overlay').addEventListener('click', () => {
      document.body.removeChild(confirmDialog);
    });
    
    // Handle delete button
    confirmDialog.querySelector('.confirm-yes').addEventListener('click', () => {
      console.log('[CustomSizeSelector] User confirmed deletion of:', sizeName);
      
      // IMMEDIATELY remove the pill from DOM before any mutations can occur
      const pillToDelete = document.querySelector(`[data-custom-size="${sizeName}"]`);
      if (pillToDelete) {
        pillToDelete.remove();
        console.log('[CustomSizeSelector] Immediately removed pill from DOM');
      }
      
      // If this was the selected size, deselect it and clear the hidden input
      if (this.selectedCustomSize === sizeName) {
        this.selectedCustomSize = null;
        this.updateCustomSizeInput('');
        console.log('[CustomSizeSelector] Cleared selection and hidden input');
      }
      
      // Remove from storage
      this.storage.removeSize(sizeName);
      
      // Update global store
      window.__customSizes = this.storage.getAllSizes();
      
      // Now re-render (but the deleted pill is already gone)
      this.renderCustomSizes();
      
      console.log('[CustomSizeSelector] Deleted custom size:', sizeName);
      document.body.removeChild(confirmDialog);
    });
    
    // Handle cancel button
    confirmDialog.querySelector('.confirm-no').addEventListener('click', () => {
      document.body.removeChild(confirmDialog);
    });
    
    // Handle edit button
    confirmDialog.querySelector('.confirm-edit').addEventListener('click', () => {
      document.body.removeChild(confirmDialog);
      const sizeData = this.storage.getSize(sizeName);
      if (sizeData && window.customMeasurementPopup) {
        window.customMeasurementPopup.openPopupForEdit(sizeData);
      }
    });
  }
  
  addNewCustomSize(sizeData) {
    this.storage.addSize(sizeData);
    
    // Update global store
    window.__customSizes = this.storage.getAllSizes();
    
    // Ensure we have a valid container before rendering
    if (!this.container || !document.body.contains(this.container)) {
      this.findContainer();
    }
    
    this.renderCustomSizes();
    
    // Only try to select if we successfully rendered
    if (this.container) {
      setTimeout(() => {
        this.selectCustomSize(sizeData.name);
      }, 100);
    }
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('customsize:added', {
      detail: sizeData
    }));
    
    console.log('[CustomSizeSelector] Added new custom size:', sizeData.name);
  }
  
  updateCustomSize(oldName, newSizeData) {
    this.storage.updateSize(oldName, newSizeData);
    
    // Update global store
    window.__customSizes = this.storage.getAllSizes();
    
    this.renderCustomSizes();
    
    // Update selection if the edited size was selected
    if (this.selectedCustomSize === oldName) {
      // This will also update the measurement properties
      this.selectCustomSize(newSizeData.name);
    }
    
    console.log('[CustomSizeSelector] Updated custom size:', oldName, '->', newSizeData.name);
  }
  
  setupContainerPersistence() {
    // Flag to prevent infinite loops
    let isRecreating = false;
    
    // Use MutationObserver to detect if our custom sizes wrapper gets removed
    const observer = new MutationObserver((mutations) => {
      // Skip if we're already recreating
      if (isRecreating) {
        return;
      }
      
      console.log('[CustomSizeSelector] === MUTATION OBSERVER TRIGGERED ===');
      console.log('[CustomSizeSelector] Mutations count:', mutations.length);
      
      // Track if we need to recreate
      let needsRecreation = false;
      
      // Before processing mutations, preserve Add Custom Size button state
      const addCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]');
      const wasInvisiblySelected = addCustomSizeRadio && 
                                   addCustomSizeRadio.closest('label') && 
                                   addCustomSizeRadio.closest('label').classList.contains('invisible-selected');
      
      // Check each mutation
      mutations.forEach((mutation, index) => {
        console.log(`[CustomSizeSelector] Mutation ${index + 1}:`, {
          type: mutation.type,
          target: mutation.target.tagName || mutation.target.nodeName,
          targetClasses: mutation.target.classList ? Array.from(mutation.target.classList) : 'N/A',
          addedNodes: mutation.addedNodes.length,
          removedNodes: mutation.removedNodes.length
        });
        
        // Check if our custom sizes wrapper was removed
        if (mutation.removedNodes.length > 0) {
          Array.from(mutation.removedNodes).forEach((node, nodeIndex) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const isOurWrapper = node.classList && node.classList.contains('custom-sizes-wrapper');
              const containsOurPills = node.querySelector && node.querySelector('.custom-size-pill');
              
              console.log(`[CustomSizeSelector] Removed node ${nodeIndex + 1}:`, {
                tagName: node.tagName,
                classes: Array.from(node.classList || []),
                isOurWrapper,
                containsOurPills
              });
              
              if (isOurWrapper || containsOurPills) {
                needsRecreation = true;
              }
            }
          });
        }
      });
      
      // If our wrapper was removed, recreate immediately
      if (needsRecreation && window.__customSizes && window.__customSizes.length > 0) {
        console.log('[CustomSizeSelector] === CONTAINER REMOVED - STARTING RECREATION ===');
        
        // Set flag to prevent infinite loop
        isRecreating = true;
        
        // Temporarily disconnect observer
        observer.disconnect();
        
        // Recreate container and re-render pills immediately
        console.log('[CustomSizeSelector] Finding container...');
        this.findContainer();
        
        console.log('[CustomSizeSelector] Rendering custom sizes...');
        this.renderCustomSizes();
        
        // Check if a standard size is currently selected
        const standardSizeSelected = document.querySelector('input[type="radio"][name*="Size"]:checked:not(.custom-size-radio)');
        const isStandardSize = standardSizeSelected && 
                             standardSizeSelected.value !== 'Custom' && 
                             standardSizeSelected.value !== 'Add Custom Size';
        
        // If a standard size is selected, make sure custom sizes are deselected
        if (isStandardSize) {
          this.deselectAllCustomSizes();
          this.selectedCustomSize = null;
        } else if (this.selectedCustomSize) {
          // Restore visual selection for custom size
          const selectedPill = document.querySelector(`[data-custom-size="${this.selectedCustomSize}"]`);
          if (selectedPill) {
            selectedPill.classList.add('visually-selected');
          }
          
          // Ensure Add Custom Size is invisibly selected
          const addCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]');
          if (addCustomSizeRadio && addCustomSizeRadio.checked) {
            const addCustomLabel = addCustomSizeRadio.closest('label');
            if (addCustomLabel) {
              addCustomLabel.classList.add('invisible-selected');
            }
          }
        }
        
        console.log('[CustomSizeSelector] === CONTAINER RECREATION COMPLETE ===');
        
        // Re-connect observer after a short delay
        setTimeout(() => {
          isRecreating = false;
          const observeTarget = findObserveTarget();
          if (observeTarget) {
            observer.observe(observeTarget, {
              childList: true,
              subtree: true
            });
          }
        }, 100);
      } else if (!needsRecreation && wasInvisiblySelected) {
        // If we don't need full recreation but the Add Custom Size button was invisibly selected,
        // restore that state immediately to prevent flicker
        setTimeout(() => {
          const newAddCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]');
          if (newAddCustomSizeRadio && newAddCustomSizeRadio.checked) {
            const label = newAddCustomSizeRadio.closest('label');
            if (label && !label.classList.contains('invisible-selected')) {
              label.classList.add('invisible-selected');
            }
          }
        }, 0);
      }
    });
    
    // Find the size variant container to observe
    const findObserveTarget = () => {
      const sizeRadios = document.querySelectorAll('input[type="radio"][name*="Size"], input[type="radio"][value="Small"], input[type="radio"][value="Medium"], input[type="radio"][value="Add Custom Size"]');
      let observeTarget = null;
      
      if (sizeRadios.length > 0) {
        const firstRadio = sizeRadios[0];
        observeTarget = firstRadio.closest('fieldset') || firstRadio.closest('.variant-option') || firstRadio.closest('[data-option]');
      }
      
      if (!observeTarget) {
        observeTarget = document.querySelector('variant-picker, .variant-picker, product-information, .product-form');
      }
      
      return observeTarget;
    };
    
    const observeTarget = findObserveTarget();
    if (observeTarget) {
      observer.observe(observeTarget, {
        childList: true,
        subtree: true
      });
      
      console.log('[CustomSizeSelector] MutationObserver monitoring started on:', observeTarget);
    } else {
      console.warn('[CustomSizeSelector] Could not find observe target, retrying...');
      setTimeout(() => this.setupContainerPersistence(), 500);
    }
    
    // Also listen for change events to handle deselection
    document.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && (e.target.name === 'Size' || e.target.name.includes('Size'))) {
        const isCustomRadio = e.target.classList.contains('custom-size-radio');
        const isAddCustomButton = e.target.value === 'Custom' || e.target.value === 'Add Custom Size';
        const isCustomValue = e.target.value.startsWith('custom-size-');
        
        if (!isCustomRadio && !isAddCustomButton) {
          // Standard size selected - deselect all custom sizes immediately
          this.deselectAllCustomSizes();
        }
      }
    });
  }
}

/**
 * Main popup functionality for collecting measurements
 */
class CustomMeasurementPopup {
  constructor() {
    this.isOpen = false;
    this.editingSize = null;
    this.popup = null;
    this.customSizeStorage = new CustomSizeStorage();
    this.sizeMatcher = new SizeMatcher();
    this.customSizeSelector = new CustomSizeSelector(this.customSizeStorage);
    this.init();
  }
  
  init() {
    try {
      console.log('[CustomMeasurementPopup] Initializing popup...');
      this.createPopup();
      this.setupEventListeners();
      console.log('[CustomMeasurementPopup] Initialization complete');
    } catch (error) {
      console.error('[CustomMeasurementPopup] Initialization error:', error);
      console.error('Stack trace:', error.stack);
    }
  }
  
  createPopup() {
    // Use existing popup from Liquid template if it exists
    const existingPopup = document.getElementById('custom-measurement-popup');
    if (existingPopup) {
      this.popup = existingPopup;
      console.log('[CustomMeasurementPopup] Using existing popup from Liquid template');
      return;
    }
    
    // Only create popup dynamically if it doesn't exist
    console.log('[CustomMeasurementPopup] Creating popup dynamically');
    this.popup = document.createElement('div');
    this.popup.id = 'custom-measurement-popup';
    this.popup.className = 'custom-measurement-popup';
    
    this.popup.innerHTML = `
      <div class="popup-overlay"></div>
      <div class="popup-content">
        <div class="popup-header">
          <h3>Add Your Measurements</h3>
          <button class="popup-close" type="button">&times;</button>
        </div>
        <div class="popup-body">
          <form id="measurement-form">
            <div class="form-group">
              <label for="custom-size-name">Size Name:</label>
              <input type="text" id="custom-size-name" name="sizeName" required maxlength="20">
              <small>Give your custom size a name (e.g., "John's Size", "Size A")</small>
            </div>
            
            <div class="measurements-grid">
              <div class="form-group">
                <label for="chest-measurement">Chest (cm):</label>
                <input type="number" id="chest-measurement" name="chest" step="0.1" min="0" max="200">
              </div>
              
              <div class="form-group">
                <label for="waist-measurement">Waist (cm):</label>
                <input type="number" id="waist-measurement" name="waist" step="0.1" min="0" max="200">
              </div>
              
              <div class="form-group">
                <label for="hip-measurement">Hip (cm):</label>
                <input type="number" id="hip-measurement" name="hip" step="0.1" min="0" max="200">
              </div>
              
              <div class="form-group">
                <label for="height-measurement">Height (cm):</label>
                <input type="number" id="height-measurement" name="height" step="0.1" min="0" max="250">
              </div>
              
              <div class="form-group">
                <label for="weight-measurement">Weight (kg):</label>
                <input type="number" id="weight-measurement" name="weight" step="0.1" min="0" max="300">
              </div>
              
              <div class="form-group">
                <label for="shoulder-measurement">Shoulder Width (cm):</label>
                <input type="number" id="shoulder-measurement" name="shoulder" step="0.1" min="0" max="100">
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn-cancel">Cancel</button>
              <button type="submit" class="btn-save">Save Measurements</button>
            </div>
          </form>
        </div>
      </div>
    `;
    
    document.body.appendChild(this.popup);
  }
  
  setupEventListeners() {
    if (!this.popup) return;
    
    // Close popup events - handle both structures
    const closeButton = this.popup.querySelector('.popup-close, .measurement-popup-close');
    const cancelButton = this.popup.querySelector('.btn-cancel, button[data-save-measurements]');
    const overlay = this.popup.querySelector('.popup-overlay, .measurement-popup-overlay, [data-close-popup]');
    
    if (closeButton) closeButton.addEventListener('click', () => this.close());
    if (cancelButton && cancelButton.textContent.includes('Cancel')) {
      cancelButton.addEventListener('click', () => this.close());
    }
    if (overlay) overlay.addEventListener('click', () => this.close());
    
    // Form submission
    const form = this.popup.querySelector('#measurement-form, .measurement-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.saveMeasurements();
      });
    }
    
    // Handle save button click for Liquid template
    const saveButton = this.popup.querySelector('[data-save-measurements]');
    if (saveButton) {
      saveButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.saveMeasurements();
      });
    }
    
    // Escape key to close popup
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Handle info icon clicks
    const infoButtons = this.popup.querySelectorAll('.info-icon');
    infoButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const field = button.getAttribute('data-info-for');
        this.showInfoPopup(field);
      });
    });
    
    // Handle measurement field changes for lead time calculation
    const measurementInputs = this.popup.querySelectorAll('.measurement-input');
    measurementInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.updateLeadTimeDisplay();
      });
    });
    
    // Handle unit change for both unit display and lead time recalculation
    const unitRadios = this.popup.querySelectorAll('input[name="measurement_unit"]');
    unitRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.updateUnitDisplay(e.target.value);
        this.updateLeadTimeDisplay();
      });
    });
    
    // Setup info popup close handlers
    const infoPopup = document.querySelector('[data-info-popup]');
    if (infoPopup) {
      const closeButtons = infoPopup.querySelectorAll('[data-info-close]');
      closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.hideInfoPopup());
      });
    }
    
    // Set initial unit display
    const checkedUnit = this.popup.querySelector('input[name="measurement_unit"]:checked');
    if (checkedUnit) {
      this.updateUnitDisplay(checkedUnit.value);
    }
    
    // Handle video play button
    const playButton = this.popup.querySelector('[data-video-play]');
    if (playButton) {
      playButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.playVideo();
      });
    }
  }
  
  playVideo() {
    console.log('[CustomMeasurementPopup] Playing video');
    
    // Find the video element
    const video = this.popup.querySelector('.measurement-video');
    const playButton = this.popup.querySelector('[data-video-play]');
    
    if (video) {
      // Hide the play button
      if (playButton) {
        playButton.style.display = 'none';
      }
      
      // Unmute and play the video
      video.muted = false;
      video.play().catch(error => {
        console.error('[CustomMeasurementPopup] Error playing video:', error);
        // Show play button again if play fails
        if (playButton) {
          playButton.style.display = 'flex';
        }
      });
      
      // Show play button again when video ends (since it loops)
      video.addEventListener('pause', () => {
        if (playButton && video.paused) {
          playButton.style.display = 'flex';
        }
      });
      
      // Hide play button when video plays
      video.addEventListener('play', () => {
        if (playButton) {
          playButton.style.display = 'none';
        }
      });
    }
  }
  
  updateUnitDisplay(unit) {
    console.log('[CustomMeasurementPopup] Updating unit display to:', unit);
    
    // Update all unit displays
    const unitDisplays = this.popup.querySelectorAll('[data-unit]');
    unitDisplays.forEach(display => {
      display.textContent = unit;
    });
    
    // Update placeholders based on unit
    const inputs = this.popup.querySelectorAll('.measurement-input[type="number"]');
    inputs.forEach(input => {
      const currentPlaceholder = input.placeholder;
      if (unit === 'inches') {
        input.placeholder = currentPlaceholder.replace(/cm/gi, 'inches').replace(/CM/g, 'Inches');
      } else {
        input.placeholder = currentPlaceholder.replace(/inches/gi, 'cm').replace(/Inches/g, 'CM');
      }
    });
  }
  
  showInfoPopup(field) {
    console.log('[CustomMeasurementPopup] Showing info for field:', field);
    
    // Get the info data from the JSON script
    const infoDataScript = document.getElementById('measurement-info-data');
    if (!infoDataScript) {
      console.error('[CustomMeasurementPopup] Info data script not found');
      return;
    }
    
    let infoData;
    try {
      infoData = JSON.parse(infoDataScript.textContent);
    } catch (e) {
      console.error('[CustomMeasurementPopup] Failed to parse info data:', e);
      return;
    }
    
    const imageUrl = infoData[field];
    if (!imageUrl) {
      console.log('[CustomMeasurementPopup] No image for field:', field);
      return;
    }
    
    // Find the info popup
    const infoPopup = document.querySelector('[data-info-popup]');
    if (!infoPopup) {
      console.error('[CustomMeasurementPopup] Info popup not found');
      return;
    }
    
    // Update the title and image
    const titleElement = infoPopup.querySelector('[data-info-title]');
    const imageElement = infoPopup.querySelector('[data-info-image]');
    
    if (titleElement) {
      // Get the field label from the main popup
      const fieldElement = this.popup.querySelector(`[data-field="${field}"] label`);
      if (fieldElement) {
        titleElement.textContent = fieldElement.textContent.split('(')[0].trim();
      }
    }
    
    if (imageElement) {
      imageElement.src = imageUrl;
      imageElement.alt = `How to measure ${field}`;
    }
    
    // Show the popup
    infoPopup.setAttribute('aria-hidden', 'false');
    infoPopup.style.display = 'flex';
  }
  
  hideInfoPopup() {
    const infoPopup = document.querySelector('[data-info-popup]');
    if (infoPopup) {
      infoPopup.setAttribute('aria-hidden', 'true');
      infoPopup.style.display = 'none';
    }
  }
  
  openPopupForCustomSize() {
    try {
      console.log('[CustomMeasurementPopup] Opening popup for new custom size');
      console.log('[CustomMeasurementPopup] Popup exists:', !!this.popup);
      console.log('[CustomMeasurementPopup] Is already open:', this.isOpen);
      
      // Don't open popup if a custom size is already selected
      if (this.customSizeSelector && this.customSizeSelector.selectedCustomSize) {
        console.log('[CustomMeasurementPopup] Custom size already selected, not opening popup');
        return;
      }
      
      this.editingSize = null;
      this.clearForm();
      this.open();
      console.log('[CustomMeasurementPopup] Popup opened successfully');
    } catch (error) {
      console.error('[CustomMeasurementPopup] Error opening popup:', error);
      console.error('Stack trace:', error.stack);
    }
  }
  
  openPopupForEdit(sizeData) {
    console.log('[CustomMeasurementPopup] Opening popup for editing:', sizeData.name);
    this.editingSize = sizeData.name;
    this.populateForm(sizeData);
    this.open();
  }
  
  open() {
    this.isOpen = true;
    this.popup.classList.add('is-open');
    
    // For Liquid template, also set aria-hidden to false
    this.popup.setAttribute('aria-hidden', 'false');
    
    document.body.style.overflow = 'hidden';
    
    // Update lead time display when opening
    setTimeout(() => {
      this.updateLeadTimeDisplay();
    }, 100);
    
    // Focus on the first input - handle both structures
    const firstInput = this.popup.querySelector('#custom-size-name, #measurement-size-label');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }
  }
  
  close() {
    this.isOpen = false;
    const wasEditingSize = this.editingSize;
    this.editingSize = null;
    this.popup.classList.remove('is-open');
    
    // For Liquid template, also set aria-hidden back to true
    this.popup.setAttribute('aria-hidden', 'true');
    
    document.body.style.overflow = '';
    
    // Check if "Add Custom Size" is selected and no measurements were saved
    const addCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]:checked');
    if (addCustomSizeRadio && !wasEditingSize) {
      // No measurements saved, convert Add to Cart button to Add Measurements
      this.convertToAddMeasurementsButton();
    }
    
    // Dispatch close event
    document.dispatchEvent(new CustomEvent('measurement-popup:close', {
      detail: { editingSize: wasEditingSize }
    }));
  }
  
  convertToAddMeasurementsButton() {
    // Only convert if "Add Custom Size" is selected
    const addCustomSizeRadio = document.querySelector('input[value="Add Custom Size"][type="radio"]:checked');
    if (!addCustomSizeRadio) return;
    
    // Find all add to cart buttons
    const addToCartButtons = document.querySelectorAll('button[type="submit"][name="add"], .button--add-to-cart, add-to-cart-component button');
    
    addToCartButtons.forEach(button => {
      // Skip if already converted
      if (button.classList.contains('measurement-button')) return;
      
      // Store original button HTML for restoration
      if (!button.dataset.originalHtml) {
        button.dataset.originalHtml = button.innerHTML;
      }
      
      // Store original button classes
      if (!button.dataset.originalClasses) {
        button.dataset.originalClasses = button.className;
      }
      
      // Store original button text
      if (!button.dataset.originalText) {
        const textElement = button.querySelector('.add-to-cart-text__content') || button;
        button.dataset.originalText = textElement.textContent.trim();
      }
      
      // Convert to measurement button
      button.classList.add('measurement-button');
      button.type = 'button'; // Change from submit to button
      
      // Remove the cart icon and added state elements
      const cartIcon = button.querySelector('.add-to-cart-icon');
      if (cartIcon && cartIcon.parentElement) {
        cartIcon.parentElement.remove();
      }
      
      const addedState = button.querySelector('.add-to-cart-text--added');
      if (addedState) {
        addedState.remove();
      }
      
      // Update text
      const textElement = button.querySelector('.add-to-cart-text__content') || button;
      textElement.textContent = 'Add Measurements';
      
      // Remove the add-to-cart component wrapper to prevent animation
      const addToCartComponent = button.closest('add-to-cart-component');
      if (addToCartComponent) {
        // Move button out of the component
        addToCartComponent.parentNode.insertBefore(button, addToCartComponent);
        addToCartComponent.style.display = 'none';
      }
      
      // Remove any existing click handlers and add new one
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      // Add a specific class to identify measurement buttons
      newButton.classList.add('is-measurement-button');
      
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openPopupForCustomSize();
      });
    });
  }
  
  restoreAddToCartButtons() {
    const measurementButtons = document.querySelectorAll('.measurement-button');
    
    measurementButtons.forEach(button => {
      // Find the hidden add-to-cart component
      const hiddenComponent = button.nextElementSibling;
      if (hiddenComponent && hiddenComponent.tagName === 'ADD-TO-CART-COMPONENT' && hiddenComponent.style.display === 'none') {
        // Create a fresh button with original HTML
        const freshButton = document.createElement('button');
        freshButton.type = 'submit';
        freshButton.name = 'add';
        freshButton.className = button.dataset.originalClasses || button.className.replace('measurement-button', '').trim();
        
        // Restore original HTML structure
        if (button.dataset.originalHtml) {
          freshButton.innerHTML = button.dataset.originalHtml;
        }
        
        // Copy necessary attributes
        if (button.id) freshButton.id = button.id;
        if (button.getAttribute('ref')) freshButton.setAttribute('ref', button.getAttribute('ref'));
        if (button.getAttribute('on:click')) freshButton.setAttribute('on:click', button.getAttribute('on:click'));
        
        // Remove the measurement button completely
        button.remove();
        
        // Move fresh button into the component
        hiddenComponent.appendChild(freshButton);
        hiddenComponent.style.display = '';
        
        // Trigger form re-initialization to restore original handlers
        const form = freshButton.closest('form');
        if (form) {
          form.dispatchEvent(new Event('variant:change', { bubbles: true }));
        }
      } else {
        // Fallback restoration for buttons without component wrapper
        // Create a completely fresh button to ensure no event listeners remain
        const freshButton = document.createElement('button');
        freshButton.type = 'submit';
        freshButton.name = 'add';
        freshButton.className = button.dataset.originalClasses || button.className.replace('measurement-button', '').trim();
        
        // Copy attributes
        if (button.id) freshButton.id = button.id;
        Array.from(button.attributes).forEach(attr => {
          if (!['class', 'type', 'name'].includes(attr.name) && !attr.name.startsWith('data-')) {
            freshButton.setAttribute(attr.name, attr.value);
          }
        });
        
        // Restore content
        if (button.dataset.originalHtml) {
          freshButton.innerHTML = button.dataset.originalHtml;
        } else if (button.dataset.originalText) {
          freshButton.innerHTML = button.innerHTML;
          const textElement = freshButton.querySelector('.add-to-cart-text__content') || freshButton;
          textElement.textContent = button.dataset.originalText;
        } else {
          freshButton.innerHTML = button.innerHTML;
        }
        
        // Replace the button completely
        button.parentNode.replaceChild(freshButton, button);
        
        // Trigger form re-initialization
        const form = freshButton.closest('form');
        if (form) {
          form.dispatchEvent(new Event('variant:change', { bubbles: true }));
        }
      }
    });
  }
  
  clearForm() {
    const form = this.popup.querySelector('#measurement-form, .measurement-form');
    if (form) {
      form.reset();
    }
  }
  
  populateForm(sizeData) {
    // Handle both possible field IDs
    const setSafeValue = (id1, id2, value) => {
      const field = document.getElementById(id1) || document.getElementById(id2);
      if (field) field.value = value || '';
    };
    
    setSafeValue('custom-size-name', 'measurement-size-label', sizeData.name);
    setSafeValue('measurement-chest', 'measurement-chest', sizeData.measurements.chest);
    setSafeValue('measurement-waist', 'measurement-waist', sizeData.measurements.waist);
    setSafeValue('measurement-hips', 'measurement-hips', sizeData.measurements.hips || sizeData.measurements.hip);
    setSafeValue('measurement-shoulders', 'measurement-shoulders', sizeData.measurements.shoulders || sizeData.measurements.shoulder);
    setSafeValue('measurement-sleeves', 'measurement-sleeves', sizeData.measurements.sleeves || sizeData.measurements.sleeve_length);
    setSafeValue('measurement-trouser-length', 'measurement-trouser-length', sizeData.measurements.trouser_length);
    setSafeValue('measurement-shirt-length', 'measurement-shirt-length', sizeData.measurements.shirt_length);
    setSafeValue('measurement-kameez-length', 'measurement-kameez-length', sizeData.measurements.kameez_length);
    setSafeValue('measurement-lehenga-length', 'measurement-lehenga-length', sizeData.measurements.lehenga_length);
    
    // Set the unit if it exists
    if (sizeData.unit) {
      const unitRadio = this.popup.querySelector(`input[name="measurement_unit"][value="${sizeData.unit}"]`);
      if (unitRadio) {
        unitRadio.checked = true;
        // Update the unit display
        this.updateUnitDisplay(sizeData.unit);
      }
    }
  }
  
  updateLeadTimeDisplay() {
    if (!this.popup || !this.sizeMatcher) return;
    
    // Get current measurements from form
    const measurements = {};
    const inputs = this.popup.querySelectorAll('.measurement-input:not(.size-label-input)');
    let hasAllMeasurements = true;
    
    inputs.forEach(input => {
      const value = input.value.trim();
      if (value) {
        measurements[input.name] = parseFloat(value);
      } else if (input.closest('.measurement-field[style*="display: none"]') === null) {
        // Only check visible fields
        hasAllMeasurements = false;
      }
    });
    
    // Get lead time display elements
    const leadTimeDisplay = this.popup.querySelector('[data-lead-time-display]');
    const leadTimeText = this.popup.querySelector('[data-lead-time-text]');
    const leadTimeMessage = this.popup.querySelector('[data-lead-time-message]');
    
    if (!leadTimeDisplay || !leadTimeText || !leadTimeMessage) return;
    
    // Only show if we have all required measurements
    if (!hasAllMeasurements || Object.keys(measurements).length === 0) {
      leadTimeDisplay.style.display = 'none';
      return;
    }
    
    // Get current unit
    const unitRadio = this.popup.querySelector('input[name="measurement_unit"]:checked');
    const unit = unitRadio ? unitRadio.value : 'cm';
    
    // Check for size match
    const matchStatus = this.sizeMatcher.checkSizeMatch(measurements, unit);
    if (!matchStatus) {
      leadTimeDisplay.style.display = 'none';
      return;
    }
    
    // Calculate and display lead time
    const leadTimeInfo = this.sizeMatcher.calculateLeadTime(matchStatus);
    if (leadTimeInfo) {
      leadTimeText.textContent = `Estimated delivery: ${leadTimeInfo.deliveryDate}`;
      leadTimeMessage.textContent = leadTimeInfo.message;
      
      // Update icon color based on status
      const icon = leadTimeDisplay.querySelector('.lead-time-icon');
      if (icon) {
        if (matchStatus === 'in_stock') {
          icon.style.color = 'var(--color-instock, #4CAF50)';
        } else {
          icon.style.color = 'var(--color-lowstock, #FF9800)';
        }
      }
      
      leadTimeDisplay.style.display = 'block';
    }
  }
  
  saveMeasurements() {
    const form = this.popup.querySelector('#measurement-form, .measurement-form');
    if (!form) {
      console.error('[CustomMeasurementPopup] Form not found');
      return;
    }
    
    const formData = new FormData(form);
    
    // DETAILED DEBUGGING: Log all form data entries
    console.log('[MEASUREMENT-DEBUG] Raw FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`[MEASUREMENT-DEBUG]   ${key}: "${value}"`);
    }
    
    // Handle both field name variations
    const sizeName = (formData.get('sizeName') || formData.get('size_label') || '').trim();
    console.log('[MEASUREMENT-DEBUG] Size name extracted:', sizeName);
    
    if (!sizeName) {
      alert('Please enter a size name');
      return;
    }
    
    // Check if size name already exists (but allow if we're editing the same size)
    if (this.customSizeStorage.sizeExists(sizeName) && sizeName !== this.editingSize) {
      alert('A custom size with this name already exists. Please choose a different name.');
      return;
    }
    
    // Get the selected unit
    const unit = formData.get('measurement_unit') || 'cm';
    console.log('[MEASUREMENT-DEBUG] Unit selected:', unit);
    
    // DETAILED DEBUGGING: Log each measurement extraction
   const measurements = {
      chest: parseFloat(formData.get('chest')) || null,
      waist: parseFloat(formData.get('waist')) || null,
      hips: parseFloat(formData.get('hips')) || null,
      shoulders: parseFloat(formData.get('shoulders')) || null,
      sleeves: parseFloat(formData.get('sleeves')) || null,
      trouser_length: parseFloat(formData.get('trouser_length')) || null,
      shirt_length: parseFloat(formData.get('shirt_length')) || null,
      kameez_length: parseFloat(formData.get('kameez_length')) || null,
      lehenga_length: parseFloat(formData.get('lehenga_length')) || null
    };
    
    console.log('[MEASUREMENT-DEBUG] Extracted measurements:');
    Object.entries(measurements).forEach(([field, value]) => {
      const rawValue = formData.get(field);
      console.log(`[MEASUREMENT-DEBUG]   ${field}: raw="${rawValue}" -> parsed=${value}`);
    });
    
    const sizeData = {
      name: sizeName,
      unit: unit,
      measurements: measurements,
      created: this.editingSize ? this.customSizeStorage.getSize(this.editingSize).created : new Date().toISOString(),
      updated: new Date().toISOString()
    };
    
    console.log('[MEASUREMENT-DEBUG] Final sizeData object:', sizeData);
    
    // Test size matching for debugging
    if (this.sizeMatcher) {
      console.log('[MEASUREMENT-DEBUG] Testing size matching...');
      const matchResult = this.sizeMatcher.checkSizeMatch(measurements, unit);
      console.log('[MEASUREMENT-DEBUG] Size match result:', matchResult);
    } else {
      console.log('[MEASUREMENT-DEBUG] No sizeMatcher available');
    }
    
    if (this.editingSize) {
      // Update existing size
      this.customSizeSelector.updateCustomSize(this.editingSize, sizeData);
      console.log('[CustomMeasurementPopup] Updated custom size:', this.editingSize, '->', sizeName);
    } else {
      // Add new size
      this.customSizeSelector.addNewCustomSize(sizeData);
      console.log('[CustomMeasurementPopup] Added new custom size:', sizeName);
    }
    
    // Restore Add to Cart buttons since we now have measurements
    this.restoreAddToCartButtons();
    
    this.close();
  }
  
  getSelectedVariant() {
    // Check if a custom size is selected
    const selectedCustomSize = document.querySelector('.custom-size-radio:checked');
    if (selectedCustomSize) {
      return { title: selectedCustomSize.value, isCustom: true };
    }
    
    // Check standard size selection
    const selectedStandardSize = document.querySelector('input[type="radio"][name*="Size"]:checked:not(.custom-size-radio)');
    if (selectedStandardSize) {
      return { title: selectedStandardSize.value, isCustom: false };
    }
    
    return null;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[CustomMeasurementPopup] DOM loaded, initializing...');
    window.customMeasurementPopup = new CustomMeasurementPopup();
  });
} else {
  console.log('[CustomMeasurementPopup] DOM already loaded, initializing...');
  window.customMeasurementPopup = new CustomMeasurementPopup();
}